import React from 'react';
import { TrailmasterCrown } from './TrailmasterCrown';

interface PersonaAvatarProps {
  name: string;
  specialization?: string;
  avatarUrl?: string;
  size?: 'small' | 'medium' | 'large';
  // SP3 I.3 — Trailmaster crown overlay. Both props optional + opt-in:
  // when personaId === trailmasterPersonaId the crown wraps the avatar
  // in a positioned shell. Existing call sites that omit both render
  // identically to the v0.3 baseline (SP3-SC-03: conditional wrap to
  // avoid breaking ~12 callers, especially TurnMarkerNodeRich whose
  // ReactFlow transform invariant per feedback_reactflow_style_transform_translate
  // depends on the avatar's NOT being wrapped).
  personaId?: string;
  trailmasterPersonaId?: string | null;
}

// Deterministic color palette for the name-hash fallback. Same name → same
// color → tile identity stays stable across renders. Used when
// primarySpecialization is missing (every seed persona today) or doesn't
// match a key in the specialization map.
const NAME_HASH_PALETTE = [
  'bg-rose-100 text-rose-700',
  'bg-orange-100 text-orange-700',
  'bg-amber-100 text-amber-700',
  'bg-yellow-100 text-yellow-700',
  'bg-lime-100 text-lime-700',
  'bg-emerald-100 text-emerald-700',
  'bg-teal-100 text-teal-700',
  'bg-cyan-100 text-cyan-700',
  'bg-sky-100 text-sky-700',
  'bg-blue-100 text-blue-700',
  'bg-indigo-100 text-indigo-700',
  'bg-violet-100 text-violet-700',
  'bg-purple-100 text-purple-700',
  'bg-fuchsia-100 text-fuchsia-700',
  'bg-pink-100 text-pink-700',
];

const colorFromName = (name: string): string => {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return NAME_HASH_PALETTE[Math.abs(h) % NAME_HASH_PALETTE.length];
};

export const PersonaAvatar: React.FC<PersonaAvatarProps> = ({
  name,
  specialization,
  avatarUrl,
  size = 'medium',
  personaId,
  trailmasterPersonaId,
}) => {
  // Determine size class
  const sizeClass = {
    small: 'w-8 h-8 text-xs',
    medium: 'w-12 h-12 text-sm',
    large: 'w-20 h-20 text-xl'
  }[size];

  // Pick a background color: prefer specialization-based mapping when present
  // and matching, else fall back to a deterministic name-hash palette so
  // every persona gets a distinct vibrant avatar even with empty data.
  const getBackgroundColor = () => {
    if (specialization) {
      const specializationMap: Record<string, string> = {
        'technical': 'bg-blue-100 text-blue-700',
        'creative': 'bg-purple-100 text-purple-700',
        'analytical': 'bg-green-100 text-green-700',
        'research': 'bg-yellow-100 text-yellow-700',
        'communication': 'bg-pink-100 text-pink-700',
        'management': 'bg-indigo-100 text-indigo-700'
      };
      const key = Object.keys(specializationMap).find(
        k => specialization.toLowerCase().includes(k)
      );
      if (key) return specializationMap[key];
    }
    return colorFromName(name);
  };

  // Get initials from name
  const getInitials = () => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const isTrailmaster = !!personaId && !!trailmasterPersonaId
    && personaId === trailmasterPersonaId;

  const avatarElement = (
    <div className={`persona-avatar ${sizeClass} rounded-full flex items-center justify-center font-bold ${getBackgroundColor()}`}>
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={name}
          className="w-full h-full rounded-full object-cover"
        />
      ) : (
        <span role="img" aria-label={name}>{getInitials()}</span>
      )}
    </div>
  );

  // SP3-SC-03: conditional wrap. The legacy avatarElement renders
  // unchanged for callers that omit personaId / trailmasterPersonaId
  // (preserves ReactFlow node-transform invariant — wrapping every
  // avatar would shift child position by the wrapper's flow space).
  if (!isTrailmaster) {
    return avatarElement;
  }
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {avatarElement}
      <TrailmasterCrown size={size} variant="corner-overlay" />
    </div>
  );
};
